import Fuse from "fuse.js";
import type { AssetResponse } from "../generated/api/types.gen";

/**
 * Fields fuzzy-searched in the asset picker. Currently just the asset name;
 * the key list can be widened later (e.g. manufacturer metadata) without
 * touching callers.
 */
const SEARCH_KEYS = ["name"];

/**
 * Cache the Fuse index per `assets` array reference so repeated queries (e.g.
 * on every keystroke) reuse the same index instead of rebuilding it O(n) each
 * time. The WeakMap lets entries be GC'd once an asset list is no longer held.
 */
const fuseCache = new WeakMap<AssetResponse[], Fuse<AssetResponse>>();

function getFuse(assets: AssetResponse[]): Fuse<AssetResponse> {
  let fuse = fuseCache.get(assets);
  if (!fuse) {
    fuse = new Fuse(assets, {
      keys: SEARCH_KEYS,
      threshold: 0.4,
      ignoreLocation: true,
    });
    fuseCache.set(assets, fuse);
  }
  return fuse;
}

/**
 * Fuzzy-rank assets against a free-text query over the asset name.
 * An empty/whitespace query returns the original list unchanged (and order).
 */
export function searchAssets(
  assets: AssetResponse[],
  query: string,
): AssetResponse[] {
  const trimmed = query.trim();
  if (!trimmed) return assets;

  return getFuse(assets)
    .search(trimmed)
    .map((result) => result.item);
}
